"use client";

import { classed, clsx } from "@trydig/ui";
import { AccordionPrimitive } from "@trydig/ui/components";
import {
	type ComponentProps,
	type PropsWithChildren,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { Chevron } from "@/components/icons/Chevron";
import { useTranslations } from "@/i18n/client";
import { ShowMoreContent, ShowMoreRoot, ShowMoreTrigger } from "../ShowMore";
import styles from "./Accordion.module.css";

export const AccordionRoot = classed(
	AccordionPrimitive.Root,
	styles.accordionRoot,
);

export const AccordionItem = classed(
	AccordionPrimitive.Item,
	styles.accordionItem,
);

export const AccordionTrigger = classed(
	AccordionPrimitive.Trigger,
	styles.accordionTrigger,
);

const AccordionContentPrimitive = classed(
	classed(AccordionPrimitive.Content, styles.accordionContent),
);

export const AccordionContent = ({
	expandable,
	children,
}: PropsWithChildren<{ expandable?: boolean }>) => {
	if (expandable) {
		return <ExpandableAccordionContent>{children}</ExpandableAccordionContent>;
	}

	return <AccordionContentPrimitive>{children}</AccordionContentPrimitive>;
};

const ExpandableAccordionContent = ({ children }: PropsWithChildren) => {
	const DESCRIPTION_MAX_HEIGHT = 160;

	const t = useTranslations();
	const elementRef = useRef<HTMLDivElement | null>(null);
	const [contentHeight, setContentHeight] = useState<number>(0);

	useLayoutEffect(() => {
		if (elementRef.current) {
			setContentHeight(elementRef.current.clientHeight);
		}
	}, []);

	if (!children) return null;

	return (
		<ShowMoreRoot>
			<ShowMoreContent collapsedHeight={DESCRIPTION_MAX_HEIGHT}>
				<AccordionPrimitive.Content
					className={styles.accordionContent}
					ref={elementRef}
				>
					{children}
				</AccordionPrimitive.Content>
			</ShowMoreContent>
			{contentHeight > DESCRIPTION_MAX_HEIGHT && (
				<ShowMoreTrigger>
					<button type="button" className={styles.readMoreButton}>
						{t("Read more")}
					</button>
				</ShowMoreTrigger>
			)}
		</ShowMoreRoot>
	);
};

export const AccordionIndicator = ({
	className,
	...props
}: ComponentProps<"div">) => (
	<div
		className={clsx(styles.accordionIndicator, className)}
		data-indicator
		{...props}
	>
		<Chevron />
	</div>
);

export type AccordionItemType = { title: string; description: React.ReactNode };

export const Accordion = ({
	className,
	items,
	indicator = true,
	...props
}: {
	className?: string;
	items: AccordionItemType[];
	indicator?: boolean;
} & ComponentProps<typeof AccordionRoot>) => {
	return (
		<AccordionRoot {...props} className={className}>
			{items.map((item) => (
				<AccordionItem key={item.title}>
					<AccordionTrigger>
						{item.title}
						{indicator && <AccordionIndicator />}
					</AccordionTrigger>
					<AccordionContent>{item.description}</AccordionContent>
				</AccordionItem>
			))}
		</AccordionRoot>
	);
};
